/**
 * TopUpBalance Component Translations
 * 
 * All translations for the TopUpBalance component across all supported languages.
 * This keeps component-specific translations close to the component.
 */

import { digitalWalletTranslations } from '@vaiu/utils';

const fundingSourceTranslations = {
  'vaiu.topup.funding.question': 'Where do you want to pull money from?',
  'vaiu.topup.funding.amount': 'Amount',
  'vaiu.topup.funding.cards_and_banks': 'Cards & banks',
  'vaiu.topup.funding.charge_card': 'Charge my card',
  'vaiu.topup.funding.charge_card_and_create': 'Charge my card and create token',
  'vaiu.topup.funding.charging_card': 'Charging my card...',
  'vaiu.topup.funding.charging_card_and_creating': 'Charging my card and creating token...',
  'vaiu.topup.funding.balance': '{currency} balance',
  'vaiu.topup.funding.balance_label': 'Balance',
  'vaiu.topup.funding.available': '{amount} {currency} available',
  'vaiu.topup.funding.default_card': 'Default card',
  'vaiu.topup.funding.saved_card': 'Saved card',
  'vaiu.topup.funding.linked_account': 'Linked account',
  'vaiu.topup.funding.paypal_checkout': 'Checkout',
  'vaiu.topup.funding.digital_wallet': 'Google or Apple Pay',
  'vaiu.topup.funding.enter_amount': 'Enter a token amount to continue.',
  'vaiu.topup.funding.ready_from_balance': 'Ready to create from this balance.',
  'vaiu.topup.funding.insufficient_balance': 'Insufficient balance',
  'vaiu.topup.funding.insufficient_detail': 'Your selected currency balance is not enough. Choose another funding source to continue.',
  'vaiu.topup.funding.create_from_balance': 'Create from balance',
};

export const topUpBalanceTranslations = {
  en: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.en,
    'vaiu.topup.title': 'Top Up Balance',
    'vaiu.topup.guest_title': 'Complete Your Payment',
    'vaiu.topup.amount_label': 'Top-up Amount ({currency})',
    'vaiu.topup.select_method': 'Select Payment Method',
    'vaiu.topup.select_method_placeholder': 'Select a payment method',
    'vaiu.topup.add_new_card': 'Add a new card',
    'vaiu.topup.add_bank_account': 'Add bank account',
    'vaiu.topup.no_methods': 'No saved payment methods',
    'vaiu.topup.add_method': 'Add Payment Method',
    'vaiu.topup.button': 'Top Up',
    'vaiu.topup.processing': 'Processing...',
    'vaiu.topup.payment_amount': 'Payment Amount',
    'vaiu.topup.choose_method': 'Choose a payment method',
    'vaiu.topup.digital_wallets': 'Digital Wallets',
    'vaiu.topup.crypto': 'Crypto',
    'vaiu.topup.shortfall': 'Shortfall:',
    'vaiu.topup.tabs.atm': 'ATM',
    'vaiu.topup.tabs.card': 'Card',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Wallet',
    'vaiu.topup.atm.description': 'Tap your bank debit card to withdraw funds from your bank account (Virtual Digital ATM)',
    'vaiu.topup.crypto.unavailable': 'Crypto payments are not available for {currency}. Please use a different payment method.',
    'vaiu.topup.success.payment': 'Payment Successful',
    'vaiu.topup.success.topup': 'Top-up Successful',
    'vaiu.topup.success.paid_amount': 'Successfully paid {amount} using {method}',
    'vaiu.topup.success.balance_added': '{amount} has been added to your balance using {method}',
    'vaiu.topup.success.card_added': 'Card added successfully',
    'vaiu.topup.success.card_processing': 'Your card has been saved and payment is being processed.',
    'vaiu.topup.success.charged': '{currency} {amount} has been charged and your balance has been updated.',
    'vaiu.topup.success.paypal_redirect': 'Redirecting to PayPal',
    'vaiu.topup.success.paypal_description': 'You will be redirected to PayPal to complete the payment.',
    'vaiu.topup.success.atm_paid': 'Successfully paid {amount} via ATM withdrawal',
    'vaiu.topup.success.atm_added': '{amount} has been added to your balance via ATM withdrawal',
    'vaiu.topup.error.payment_failed': 'Payment Failed',
    'vaiu.topup.error.topup_failed': 'Top-up Failed',
    'vaiu.topup.error.digital_wallet': 'Failed to process digital wallet payment',
    'vaiu.topup.error.generic': 'Failed to process payment',
    'vaiu.topup.error.balance': 'Failed to top up balance',
    'vaiu.topup.error.card': 'Failed to process the new card. Please try again.',
    'vaiu.topup.error.atm': 'Failed to process ATM withdrawal',
    'vaiu.topup.error.select_method': 'Please select a payment method',
    'vaiu.topup.error.method_not_found': 'Selected payment method not found',
    'vaiu.topup.error.minimum_payment': 'Minimum payment amount is {amount}',
    'vaiu.topup.error.minimum_topup': 'Minimum top-up amount is {amount}',
    'vaiu.topup.error.unsupported_method': 'Unsupported payment method type',
    'vaiu.topup.error.load_methods': 'Failed to load payment methods',
    'vaiu.topup.error.atm_failed': 'ATM withdrawal failed',
    'vaiu.topup.error.api_error': 'API Error: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Payment Not Successful',
    'vaiu.topup.toast.processing_wallet': 'Processing {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Processing {{currency}} {{amount}} payment...',
    'vaiu.topup.toast.connecting': 'Connecting to payment processor',
    'vaiu.topup.toast.please_wait': 'Please wait...',
    'vaiu.topup.toast.paypal_redirect': 'PayPal Redirect',
    'vaiu.topup.toast.returning_to': 'Returning to: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Bitcoin Payment Initiated',
    'vaiu.topup.toast.bitcoin_complete': 'Please complete the transaction in your Bitcoin wallet.',
  },
  fr: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.fr,
    'vaiu.topup.title': 'Recharger le solde',
    'vaiu.topup.guest_title': 'Finaliser votre paiement',
    'vaiu.topup.amount_label': 'Montant de recharge ({currency})',
    'vaiu.topup.select_method': 'Sélectionner un mode de paiement',
    'vaiu.topup.select_method_placeholder': 'Sélectionner un mode de paiement',
    'vaiu.topup.add_new_card': 'Ajouter une nouvelle carte',
    'vaiu.topup.add_bank_account': 'Ajouter un compte bancaire',
    'vaiu.topup.no_methods': 'Aucun mode de paiement enregistré',
    'vaiu.topup.add_method': 'Ajouter un mode de paiement',
    'vaiu.topup.button': 'Recharger',
    'vaiu.topup.processing': 'Traitement...',
    'vaiu.topup.payment_amount': 'Montant du paiement',
    'vaiu.topup.choose_method': 'Choisir un mode de paiement',
    'vaiu.topup.digital_wallets': 'Portefeuilles numériques',
    'vaiu.topup.crypto': 'Crypto',
    'vaiu.topup.shortfall': 'Déficit:',
    'vaiu.topup.tabs.atm': 'DAB',
    'vaiu.topup.tabs.card': 'Carte',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Portefeuille',
    'vaiu.topup.atm.description': 'Tapez votre carte de débit bancaire pour retirer des fonds de votre compte bancaire (DAB virtuel)',
    'vaiu.topup.crypto.unavailable': 'Les paiements en crypto-monnaie ne sont pas disponibles pour {currency}. Veuillez utiliser un autre mode de paiement.',
    'vaiu.topup.success.payment': 'Paiement réussi',
    'vaiu.topup.success.topup': 'Rechargement réussi',
    'vaiu.topup.success.paid_amount': 'Paiement de {amount} effectué avec succès en utilisant {method}',
    'vaiu.topup.success.balance_added': '{amount} a été ajouté à votre solde en utilisant {method}',
    'vaiu.topup.success.card_added': 'Carte ajoutée avec succès',
    'vaiu.topup.success.card_processing': 'Votre carte a été enregistrée et le paiement est en cours de traitement.',
    'vaiu.topup.success.charged': '{currency} {amount} a été débité et votre solde a été mis à jour.',
    'vaiu.topup.success.paypal_redirect': 'Redirection vers PayPal',
    'vaiu.topup.success.paypal_description': 'Vous allez être redirigé vers PayPal pour finaliser le paiement.',
    'vaiu.topup.success.atm_paid': 'Paiement de {amount} effectué avec succès via retrait DAB',
    'vaiu.topup.success.atm_added': '{amount} a été ajouté à votre solde via retrait DAB',
    'vaiu.topup.error.payment_failed': 'Échec du paiement',
    'vaiu.topup.error.topup_failed': 'Échec du rechargement',
    'vaiu.topup.error.digital_wallet': 'Échec du traitement du paiement par portefeuille numérique',
    'vaiu.topup.error.generic': 'Échec du traitement du paiement',
    'vaiu.topup.error.balance': 'Échec du rechargement du solde',
    'vaiu.topup.error.card': 'Échec du traitement de la nouvelle carte. Veuillez réessayer.',
    'vaiu.topup.error.atm': 'Échec du traitement du retrait DAB',
    'vaiu.topup.error.select_method': 'Veuillez sélectionner un mode de paiement',
    'vaiu.topup.error.method_not_found': 'Mode de paiement sélectionné introuvable',
    'vaiu.topup.error.minimum_payment': 'Le montant minimum de paiement est {amount}',
    'vaiu.topup.error.minimum_topup': 'Le montant minimum de recharge est {amount}',
    'vaiu.topup.error.unsupported_method': 'Type de mode de paiement non pris en charge',
    'vaiu.topup.error.load_methods': 'Échec du chargement des modes de paiement',
    'vaiu.topup.error.atm_failed': 'Échec du retrait DAB',
    'vaiu.topup.error.api_error': 'Erreur API : {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Paiement non réussi',
    'vaiu.topup.toast.processing_wallet': 'Traitement de {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Traitement du paiement de {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Connexion au processeur de paiement',
    'vaiu.topup.toast.please_wait': 'Veuillez patienter...',
    'vaiu.topup.toast.paypal_redirect': 'Redirection PayPal',
    'vaiu.topup.toast.returning_to': 'Retour vers : {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Paiement Bitcoin initié',
    'vaiu.topup.toast.bitcoin_complete': 'Veuillez compléter la transaction dans votre portefeuille Bitcoin.',
  },
  es: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.es,
    'vaiu.topup.title': 'Recargar Saldo',
    'vaiu.topup.guest_title': 'Completar tu Pago',
    'vaiu.topup.amount_label': 'Monto de Recarga ({currency})',
    'vaiu.topup.select_method': 'Seleccionar Método de Pago',
    'vaiu.topup.select_method_placeholder': 'Seleccionar un método de pago',
    'vaiu.topup.add_new_card': 'Agregar una nueva tarjeta',
    'vaiu.topup.add_bank_account': 'Agregar una cuenta bancaria',
    'vaiu.topup.no_methods': 'No hay métodos de pago guardados',
    'vaiu.topup.add_method': 'Agregar Método de Pago',
    'vaiu.topup.button': 'Recargar',
    'vaiu.topup.processing': 'Procesando...',
    'vaiu.topup.payment_amount': 'Monto del Pago',
    'vaiu.topup.choose_method': 'Elige un método de pago',
    'vaiu.topup.digital_wallets': 'Billeteras Digitales',
    'vaiu.topup.crypto': 'Cripto',
    'vaiu.topup.shortfall': 'Déficit:',
    'vaiu.topup.tabs.atm': 'Cajero',
    'vaiu.topup.tabs.card': 'Tarjeta',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Billetera',
    'vaiu.topup.atm.description': 'Toca tu tarjeta de débito bancaria para retirar fondos de tu cuenta bancaria (Cajero Virtual Digital)',
    'vaiu.topup.crypto.unavailable': 'Los pagos con criptomonedas no están disponibles para {currency}. Por favor usa un método de pago diferente.',
    'vaiu.topup.success.payment': 'Pago Exitoso',
    'vaiu.topup.success.topup': 'Recarga Exitosa',
    'vaiu.topup.success.paid_amount': 'Pago de {amount} realizado exitosamente usando {method}',
    'vaiu.topup.success.balance_added': '{amount} ha sido agregado a tu saldo usando {method}',
    'vaiu.topup.success.card_added': 'Tarjeta agregada exitosamente',
    'vaiu.topup.success.card_processing': 'Tu tarjeta ha sido guardada y el pago está siendo procesado.',
    'vaiu.topup.success.charged': '{currency} {amount} ha sido cobrado y tu saldo ha sido actualizado.',
    'vaiu.topup.success.paypal_redirect': 'Redirigiendo a PayPal',
    'vaiu.topup.success.paypal_description': 'Serás redirigido a PayPal para completar el pago.',
    'vaiu.topup.success.atm_paid': 'Pago de {amount} realizado exitosamente vía retiro de cajero',
    'vaiu.topup.success.atm_added': '{amount} ha sido agregado a tu saldo vía retiro de cajero',
    'vaiu.topup.error.payment_failed': 'Pago Fallido',
    'vaiu.topup.error.topup_failed': 'Recarga Fallida',
    'vaiu.topup.error.digital_wallet': 'Error al procesar el pago con billetera digital',
    'vaiu.topup.error.generic': 'Error al procesar el pago',
    'vaiu.topup.error.balance': 'Error al recargar el saldo',
    'vaiu.topup.error.card': 'Error al procesar la nueva tarjeta. Por favor intenta de nuevo.',
    'vaiu.topup.error.atm': 'Error al procesar el retiro del cajero',
    'vaiu.topup.error.select_method': 'Por favor selecciona un método de pago',
    'vaiu.topup.error.method_not_found': 'Método de pago seleccionado no encontrado',
    'vaiu.topup.error.minimum_payment': 'El monto mínimo de pago es {amount}',
    'vaiu.topup.error.minimum_topup': 'El monto mínimo de recarga es {amount}',
    'vaiu.topup.error.unsupported_method': 'Tipo de método de pago no soportado',
    'vaiu.topup.error.load_methods': 'Error al cargar los métodos de pago',
    'vaiu.topup.error.atm_failed': 'Error en el retiro del cajero',
    'vaiu.topup.error.api_error': 'Error de API: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Pago no exitoso',
    'vaiu.topup.toast.processing_wallet': 'Procesando {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Procesando pago de {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Conectando al procesador de pagos',
    'vaiu.topup.toast.please_wait': 'Por favor espere...',
    'vaiu.topup.toast.paypal_redirect': 'Redirección de PayPal',
    'vaiu.topup.toast.returning_to': 'Volviendo a: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Pago Bitcoin iniciado',
    'vaiu.topup.toast.bitcoin_complete': 'Por favor complete la transacción en su billetera Bitcoin.',
  },
  sq: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.sq,
    'vaiu.topup.title': 'Rimbushje e Balancës',
    'vaiu.topup.guest_title': 'Plotësoni Pagesën',
    'vaiu.topup.amount_label': 'Shuma e Rimbushjes ({currency})',
    'vaiu.topup.select_method': 'Zgjidhni Metodën e Pagesës',
    'vaiu.topup.select_method_placeholder': 'Zgjidhni një metodë pagese',
    'vaiu.topup.add_new_card': 'Shtoni një kartë të re',
    'vaiu.topup.add_bank_account': 'Shtoni një llogari bankare',
    'vaiu.topup.no_methods': 'Nuk ka metoda pagese të ruajtura',
    'vaiu.topup.add_method': 'Shtoni Metodë Pagese',
    'vaiu.topup.button': 'Rimbush',
    'vaiu.topup.processing': 'Duke përpunuar...',
    'vaiu.topup.payment_amount': 'Shuma e Pagesës',
    'vaiu.topup.choose_method': 'Zgjidhni një metodë pagese',
    'vaiu.topup.digital_wallets': 'Portofolë Digjitale',
    'vaiu.topup.crypto': 'Kripto',
    'vaiu.topup.shortfall': 'Mungesa:',
    'vaiu.topup.tabs.atm': 'ATM',
    'vaiu.topup.tabs.card': 'Kartë',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Portofol',
    'vaiu.topup.atm.description': 'Prekni kartën tuaj të debitit bankar për të tërhequr fonde nga llogaria juaj bankare (ATM Virtual Digjitale)',
    'vaiu.topup.crypto.unavailable': 'Pagesat me kripto nuk janë të disponueshme për {currency}. Ju lutemi përdorni një metodë tjetër pagese.',
    'vaiu.topup.success.payment': 'Pagesa e Suksesshme',
    'vaiu.topup.success.topup': 'Rimbushja e Suksesshme',
    'vaiu.topup.success.paid_amount': 'Pagesuar me sukses {amount} duke përdorur {method}',
    'vaiu.topup.success.balance_added': '{amount} është shtuar në balancën tuaj duke përdorur {method}',
    'vaiu.topup.success.card_added': 'Karta u shtua me sukses',
    'vaiu.topup.success.card_processing': 'Karta juaj është ruajtur dhe pagesa po përpunohet.',
    'vaiu.topup.success.charged': '{currency} {amount} është ngarkuar dhe balanca juaj është përditësuar.',
    'vaiu.topup.success.paypal_redirect': 'Ridrejtim në PayPal',
    'vaiu.topup.success.paypal_description': 'Do të ridrejtoheni në PayPal për të përfunduar pagesën.',
    'vaiu.topup.success.atm_paid': 'Pagesuar me sukses {amount} përmes tërheqjes nga ATM',
    'vaiu.topup.success.atm_added': '{amount} është shtuar në balancën tuaj përmes tërheqjes nga ATM',
    'vaiu.topup.error.payment_failed': 'Pagesa Dështoi',
    'vaiu.topup.error.topup_failed': 'Rimbushja Dështoi',
    'vaiu.topup.error.digital_wallet': 'Dështoi përpunimi i pagesës me portofol digjital',
    'vaiu.topup.error.generic': 'Dështoi përpunimi i pagesës',
    'vaiu.topup.error.balance': 'Dështoi rimbushja e balancës',
    'vaiu.topup.error.card': 'Dështoi përpunimi i kartës së re. Ju lutemi provoni përsëri.',
    'vaiu.topup.error.atm': 'Dështoi përpunimi i tërheqjes nga ATM',
    'vaiu.topup.error.select_method': 'Ju lutemi zgjidhni një metodë pagese',
    'vaiu.topup.error.method_not_found': 'Metoda e pagesës e zgjedhur nuk u gjet',
    'vaiu.topup.error.minimum_payment': 'Shuma minimale e pagesës është {amount}',
    'vaiu.topup.error.minimum_topup': 'Shuma minimale e rimbushjes është {amount}',
    'vaiu.topup.error.unsupported_method': 'Lloji i metodës së pagesës nuk mbështetet',
    'vaiu.topup.error.load_methods': 'Dështoi ngarkimi i metodave të pagesës',
    'vaiu.topup.error.atm_failed': 'Tërheqja nga ATM dështoi',
    'vaiu.topup.error.api_error': 'Gabim API: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Pagesa nuk u realizua',
    'vaiu.topup.toast.processing_wallet': 'Duke përpunuar {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Duke përpunuar pagesën {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Duke u lidhur me procesuesin e pagesave',
    'vaiu.topup.toast.please_wait': 'Ju lutemi prisni...',
    'vaiu.topup.toast.paypal_redirect': 'Ridrejtim PayPal',
    'vaiu.topup.toast.returning_to': 'Duke u kthyer te: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Pagesa Bitcoin u nis',
    'vaiu.topup.toast.bitcoin_complete': 'Ju lutemi përfundoni transaksionin në portofolin tuaj Bitcoin.',
  },
  ru: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.ru,
    'vaiu.topup.title': 'Пополнить баланс',
    'vaiu.topup.guest_title': 'Завершить платеж',
    'vaiu.topup.amount_label': 'Сумма пополнения ({currency})',
    'vaiu.topup.select_method': 'Выберите способ оплаты',
    'vaiu.topup.select_method_placeholder': 'Выберите способ оплаты',
    'vaiu.topup.add_new_card': 'Добавить новую карту',
    'vaiu.topup.add_bank_account': 'Добавить банковский счет',
    'vaiu.topup.no_methods': 'Нет сохраненных способов оплаты',
    'vaiu.topup.add_method': 'Добавить способ оплаты',
    'vaiu.topup.button': 'Пополнить',
    'vaiu.topup.processing': 'Обработка...',
    'vaiu.topup.payment_amount': 'Сумма платежа',
    'vaiu.topup.choose_method': 'Выберите способ оплаты',
    'vaiu.topup.digital_wallets': 'Цифровые кошельки',
    'vaiu.topup.crypto': 'Криптовалюта',
    'vaiu.topup.shortfall': 'Недостача:',
    'vaiu.topup.tabs.atm': 'Банкомат',
    'vaiu.topup.tabs.card': 'Карта',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Кошелек',
    'vaiu.topup.atm.description': 'Приложите банковскую дебетовую карту для снятия средств с вашего банковского счета (Виртуальный цифровой банкомат)',
    'vaiu.topup.crypto.unavailable': 'Криптоплатежи недоступны для {currency}. Пожалуйста, используйте другой способ оплаты.',
    'vaiu.topup.success.payment': 'Платеж успешен',
    'vaiu.topup.success.topup': 'Пополнение успешно',
    'vaiu.topup.success.paid_amount': 'Успешно оплачено {amount} с использованием {method}',
    'vaiu.topup.success.balance_added': '{amount} добавлено на ваш баланс с использованием {method}',
    'vaiu.topup.success.card_added': 'Карта успешно добавлена',
    'vaiu.topup.success.card_processing': 'Ваша карта сохранена и платеж обрабатывается.',
    'vaiu.topup.success.charged': '{currency} {amount} списано и ваш баланс обновлен.',
    'vaiu.topup.success.paypal_redirect': 'Перенаправление в PayPal',
    'vaiu.topup.success.paypal_description': 'Вы будете перенаправлены в PayPal для завершения платежа.',
    'vaiu.topup.success.atm_paid': 'Успешно оплачено {amount} через снятие в банкомате',
    'vaiu.topup.success.atm_added': '{amount} добавлено на ваш баланс через снятие в банкомате',
    'vaiu.topup.error.payment_failed': 'Платеж не удался',
    'vaiu.topup.error.topup_failed': 'Пополнение не удалось',
    'vaiu.topup.error.digital_wallet': 'Не удалось обработать платеж через цифровой кошелек',
    'vaiu.topup.error.generic': 'Не удалось обработать платеж',
    'vaiu.topup.error.balance': 'Не удалось пополнить баланс',
    'vaiu.topup.error.card': 'Не удалось обработать новую карту. Пожалуйста, попробуйте снова.',
    'vaiu.topup.error.atm': 'Не удалось обработать снятие в банкомате',
    'vaiu.topup.error.select_method': 'Пожалуйста, выберите способ оплаты',
    'vaiu.topup.error.method_not_found': 'Выбранный способ оплаты не найден',
    'vaiu.topup.error.minimum_payment': 'Минимальная сумма платежа составляет {amount}',
    'vaiu.topup.error.minimum_topup': 'Минимальная сумма пополнения составляет {amount}',
    'vaiu.topup.error.unsupported_method': 'Неподдерживаемый тип способа оплаты',
    'vaiu.topup.error.load_methods': 'Не удалось загрузить способы оплаты',
    'vaiu.topup.error.atm_failed': 'Снятие в банкомате не удалось',
    'vaiu.topup.error.api_error': 'Ошибка API: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Платеж не выполнен',
    'vaiu.topup.toast.processing_wallet': 'Обработка {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Обработка платежа {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Подключение к платежному процессору',
    'vaiu.topup.toast.please_wait': 'Пожалуйста, подождите...',
    'vaiu.topup.toast.paypal_redirect': 'Перенаправление PayPal',
    'vaiu.topup.toast.returning_to': 'Возврат к: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Платеж Bitcoin инициирован',
    'vaiu.topup.toast.bitcoin_complete': 'Пожалуйста, завершите транзакцию в вашем Bitcoin кошельке.',
  },
  pt: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.pt,
    'vaiu.topup.title': 'Recarregar Saldo',
    'vaiu.topup.guest_title': 'Complete seu Pagamento',
    'vaiu.topup.amount_label': 'Valor do Recarga ({currency})',
    'vaiu.topup.select_method': 'Selecionar Método de Pagamento',
    'vaiu.topup.select_method_placeholder': 'Selecionar um método de pagamento',
    'vaiu.topup.add_new_card': 'Adicionar um novo cartão',
    'vaiu.topup.add_bank_account': 'Adicionar conta bancária',
    'vaiu.topup.no_methods': 'Nenhum método de pagamento salvo',
    'vaiu.topup.add_method': 'Adicionar Método de Pagamento',
    'vaiu.topup.button': 'Recarregar',
    'vaiu.topup.processing': 'Processando...',
    'vaiu.topup.payment_amount': 'Valor do Pagamento',
    'vaiu.topup.choose_method': 'Escolha um método de pagamento',
    'vaiu.topup.digital_wallets': 'Carteiras Digitais',
    'vaiu.topup.crypto': 'Cripto',
    'vaiu.topup.shortfall': 'Déficit:',
    'vaiu.topup.tabs.atm': 'Caixa Eletrônico',
    'vaiu.topup.tabs.card': 'Cartão',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Carteira',
    'vaiu.topup.atm.description': 'Toque seu cartão de débito bancário para retirar fundos da sua conta bancária (Caixa Eletrônico Virtual Digital)',
    'vaiu.topup.crypto.unavailable': 'Pagamentos com criptomoedas não estão disponíveis para {currency}. Por favor, use um método de pagamento diferente.',
    'vaiu.topup.success.payment': 'Pagamento Bem-sucedido',
    'vaiu.topup.success.topup': 'Recarga Bem-sucedida',
    'vaiu.topup.success.paid_amount': 'Pagamento de {amount} realizado com sucesso usando {method}',
    'vaiu.topup.success.balance_added': '{amount} foi adicionado ao seu saldo usando {method}',
    'vaiu.topup.success.card_added': 'Cartão adicionado com sucesso',
    'vaiu.topup.success.card_processing': 'Seu cartão foi salvo e o pagamento está sendo processado.',
    'vaiu.topup.success.charged': '{currency} {amount} foi cobrado e seu saldo foi atualizado.',
    'vaiu.topup.success.paypal_redirect': 'Redirecionando para PayPal',
    'vaiu.topup.success.paypal_description': 'Você será redirecionado para o PayPal para completar o pagamento.',
    'vaiu.topup.success.atm_paid': 'Pagamento de {amount} realizado com sucesso via saque em caixa eletrônico',
    'vaiu.topup.success.atm_added': '{amount} foi adicionado ao seu saldo via saque em caixa eletrônico',
    'vaiu.topup.error.payment_failed': 'Pagamento Falhou',
    'vaiu.topup.error.topup_failed': 'Recarga Falhou',
    'vaiu.topup.error.digital_wallet': 'Falha ao processar pagamento com carteira digital',
    'vaiu.topup.error.generic': 'Falha ao processar pagamento',
    'vaiu.topup.error.balance': 'Falha ao recarregar saldo',
    'vaiu.topup.error.card': 'Falha ao processar o novo cartão. Por favor, tente novamente.',
    'vaiu.topup.error.atm': 'Falha ao processar saque em caixa eletrônico',
    'vaiu.topup.error.select_method': 'Por favor, selecione um método de pagamento',
    'vaiu.topup.error.method_not_found': 'Método de pagamento selecionado não encontrado',
    'vaiu.topup.error.minimum_payment': 'O valor mínimo de pagamento é {amount}',
    'vaiu.topup.error.minimum_topup': 'O valor mínimo de recarga é {amount}',
    'vaiu.topup.error.unsupported_method': 'Tipo de método de pagamento não suportado',
    'vaiu.topup.error.load_methods': 'Falha ao carregar métodos de pagamento',
    'vaiu.topup.error.atm_failed': 'Saque em caixa eletrônico falhou',
    'vaiu.topup.error.api_error': 'Erro de API: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Pagamento Não Bem-sucedido',
    'vaiu.topup.toast.processing_wallet': 'Processando {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Processando pagamento de {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Conectando ao processador de pagamento',
    'vaiu.topup.toast.please_wait': 'Por favor, aguarde...',
    'vaiu.topup.toast.paypal_redirect': 'Redirecionamento PayPal',
    'vaiu.topup.toast.returning_to': 'Retornando para: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Pagamento Bitcoin Iniciado',
    'vaiu.topup.toast.bitcoin_complete': 'Por favor, complete a transação na sua carteira Bitcoin.',
  },
  el: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.el,
    'vaiu.topup.title': 'Ανανέωση Υπόλοιπου',
    'vaiu.topup.guest_title': 'Ολοκλήρωση Πληρωμής',
    'vaiu.topup.amount_label': 'Ποσό Ανανέωσης ({currency})',
    'vaiu.topup.select_method': 'Επιλέξτε Μέθοδο Πληρωμής',
    'vaiu.topup.select_method_placeholder': 'Επιλέξτε μια μέθοδο πληρωμής',
    'vaiu.topup.add_new_card': 'Προσθήκη νέας κάρτας',
    'vaiu.topup.add_bank_account': 'Προσθήκη τραπεζικού λογαριασμού',
    'vaiu.topup.no_methods': 'Δεν υπάρχουν αποθηκευμένες μέθοδοι πληρωμής',
    'vaiu.topup.add_method': 'Προσθήκη Μεθόδου Πληρωμής',
    'vaiu.topup.button': 'Ανανέωση',
    'vaiu.topup.processing': 'Επεξεργασία...',
    'vaiu.topup.payment_amount': 'Ποσό Πληρωμής',
    'vaiu.topup.choose_method': 'Επιλέξτε μια μέθοδο πληρωμής',
    'vaiu.topup.digital_wallets': 'Ψηφιακά Πορτοφόλια',
    'vaiu.topup.crypto': 'Κρύπτο',
    'vaiu.topup.shortfall': 'Ελλείψη:',
    'vaiu.topup.tabs.atm': 'ΑΤΜ',
    'vaiu.topup.tabs.card': 'Κάρτα',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Πορτοφόλι',
    'vaiu.topup.atm.description': 'Αγγίξτε την τραπεζική χρεωστική σας κάρτα για να αποσύρετε κεφάλαια από τον τραπεζικό σας λογαριασμό (Εικονικό Ψηφιακό ΑΤΜ)',
    'vaiu.topup.crypto.unavailable': 'Οι πληρωμές με κρυπτονομίσματα δεν είναι διαθέσιμες για {currency}. Παρακαλώ χρησιμοποιήστε μια διαφορετική μέθοδο πληρωμής.',
    'vaiu.topup.success.payment': 'Επιτυχής Πληρωμή',
    'vaiu.topup.success.topup': 'Επιτυχής Ανανέωση',
    'vaiu.topup.success.paid_amount': 'Πληρωμή {amount} ολοκληρώθηκε με επιτυχία χρησιμοποιώντας {method}',
    'vaiu.topup.success.balance_added': '{amount} προστέθηκε στο υπόλοιπό σας χρησιμοποιώντας {method}',
    'vaiu.topup.success.card_added': 'Η κάρτα προστέθηκε με επιτυχία',
    'vaiu.topup.success.card_processing': 'Η κάρτα σας αποθηκεύτηκε και η πληρωμή επεξεργάζεται.',
    'vaiu.topup.success.charged': '{currency} {amount} χρεώθηκε και το υπόλοιπό σας ενημερώθηκε.',
    'vaiu.topup.success.paypal_redirect': 'Ανακατεύθυνση στο PayPal',
    'vaiu.topup.success.paypal_description': 'Θα ανακατευθυνθείτε στο PayPal για να ολοκληρώσετε την πληρωμή.',
    'vaiu.topup.success.atm_paid': 'Πληρωμή {amount} ολοκληρώθηκε με επιτυχία μέσω ανάληψης ΑΤΜ',
    'vaiu.topup.success.atm_added': '{amount} προστέθηκε στο υπόλοιπό σας μέσω ανάληψης ΑΤΜ',
    'vaiu.topup.error.payment_failed': 'Αποτυχία Πληρωμής',
    'vaiu.topup.error.topup_failed': 'Αποτυχία Ανανέωσης',
    'vaiu.topup.error.digital_wallet': 'Αποτυχία επεξεργασίας πληρωμής με ψηφιακό πορτοφόλι',
    'vaiu.topup.error.generic': 'Αποτυχία επεξεργασίας πληρωμής',
    'vaiu.topup.error.balance': 'Αποτυχία ανανέωσης υπολοίπου',
    'vaiu.topup.error.card': 'Αποτυχία επεξεργασίας νέας κάρτας. Παρακαλώ δοκιμάστε ξανά.',
    'vaiu.topup.error.atm': 'Αποτυχία επεξεργασίας ανάληψης ΑΤΜ',
    'vaiu.topup.error.select_method': 'Παρακαλώ επιλέξτε μια μέθοδο πληρωμής',
    'vaiu.topup.error.method_not_found': 'Η επιλεγμένη μέθοδος πληρωμής δεν βρέθηκε',
    'vaiu.topup.error.minimum_payment': 'Το ελάχιστο ποσό πληρωμής είναι {amount}',
    'vaiu.topup.error.minimum_topup': 'Το ελάχιστο ποσό ανανέωσης είναι {amount}',
    'vaiu.topup.error.unsupported_method': 'Μη υποστηριζόμενος τύπος μεθόδου πληρωμής',
    'vaiu.topup.error.load_methods': 'Αποτυχία φόρτωσης μεθόδων πληρωμής',
    'vaiu.topup.error.atm_failed': 'Η ανάληψη ΑΤΜ απέτυχε',
    'vaiu.topup.error.api_error': 'Σφάλμα API: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Η Πληρωμή Δεν Ήταν Επιτυχής',
    'vaiu.topup.toast.processing_wallet': 'Επεξεργασία {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Επεξεργασία πληρωμής {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Σύνδεση με τον επεξεργαστή πληρωμών',
    'vaiu.topup.toast.please_wait': 'Παρακαλώ περιμένετε...',
    'vaiu.topup.toast.paypal_redirect': 'Ανακατεύθυνση PayPal',
    'vaiu.topup.toast.returning_to': 'Επιστροφή σε: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Πληρωμή Bitcoin Ξεκίνησε',
    'vaiu.topup.toast.bitcoin_complete': 'Παρακαλώ ολοκληρώστε τη συναλλαγή στο πορτοφόλι Bitcoin σας.',
  },
  de: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.de,
    'vaiu.topup.title': 'Guthaben aufladen',
    'vaiu.topup.guest_title': 'Zahlung abschließen',
    'vaiu.topup.amount_label': 'Aufladebetrag ({currency})',
    'vaiu.topup.select_method': 'Zahlungsmethode auswählen',
    'vaiu.topup.select_method_placeholder': 'Zahlungsmethode auswählen',
    'vaiu.topup.add_new_card': 'Neue Karte hinzufügen',
    'vaiu.topup.add_bank_account': 'Bankkonto hinzufügen',
    'vaiu.topup.no_methods': 'Keine gespeicherten Zahlungsmethoden',
    'vaiu.topup.add_method': 'Zahlungsmethode hinzufügen',
    'vaiu.topup.button': 'Aufladen',
    'vaiu.topup.processing': 'Wird verarbeitet...',
    'vaiu.topup.payment_amount': 'Zahlungsbetrag',
    'vaiu.topup.choose_method': 'Wählen Sie eine Zahlungsmethode',
    'vaiu.topup.digital_wallets': 'Digitale Geldbörsen',
    'vaiu.topup.crypto': 'Krypto',
    'vaiu.topup.shortfall': 'Fehlbetrag:',
    'vaiu.topup.tabs.atm': 'Geldautomat',
    'vaiu.topup.tabs.card': 'Karte',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Geldbörse',
    'vaiu.topup.atm.description': 'Tippen Sie Ihre Bank-Debitkarte, um Geld von Ihrem Bankkonto abzuheben (Virtueller Digitaler Geldautomat)',
    'vaiu.topup.crypto.unavailable': 'Kryptozahlungen sind für {currency} nicht verfügbar. Bitte verwenden Sie eine andere Zahlungsmethode.',
    'vaiu.topup.success.payment': 'Zahlung erfolgreich',
    'vaiu.topup.success.topup': 'Aufladung erfolgreich',
    'vaiu.topup.success.paid_amount': 'Erfolgreich {amount} mit {method} bezahlt',
    'vaiu.topup.success.balance_added': '{amount} wurde Ihrem Guthaben mit {method} hinzugefügt',
    'vaiu.topup.success.card_added': 'Karte erfolgreich hinzugefügt',
    'vaiu.topup.success.card_processing': 'Ihre Karte wurde gespeichert und die Zahlung wird verarbeitet.',
    'vaiu.topup.success.charged': '{currency} {amount} wurde belastet und Ihr Guthaben wurde aktualisiert.',
    'vaiu.topup.success.paypal_redirect': 'Weiterleitung zu PayPal',
    'vaiu.topup.success.paypal_description': 'Sie werden zu PayPal weitergeleitet, um die Zahlung abzuschließen.',
    'vaiu.topup.success.atm_paid': 'Erfolgreich {amount} über Geldautomaten-Abhebung bezahlt',
    'vaiu.topup.success.atm_added': '{amount} wurde Ihrem Guthaben über Geldautomaten-Abhebung hinzugefügt',
    'vaiu.topup.error.payment_failed': 'Zahlung fehlgeschlagen',
    'vaiu.topup.error.topup_failed': 'Aufladung fehlgeschlagen',
    'vaiu.topup.error.digital_wallet': 'Verarbeitung der Zahlung mit digitaler Geldbörse fehlgeschlagen',
    'vaiu.topup.error.generic': 'Verarbeitung der Zahlung fehlgeschlagen',
    'vaiu.topup.error.balance': 'Aufladung des Guthabens fehlgeschlagen',
    'vaiu.topup.error.card': 'Verarbeitung der neuen Karte fehlgeschlagen. Bitte versuchen Sie es erneut.',
    'vaiu.topup.error.atm': 'Verarbeitung der Geldautomaten-Abhebung fehlgeschlagen',
    'vaiu.topup.error.select_method': 'Bitte wählen Sie eine Zahlungsmethode',
    'vaiu.topup.error.method_not_found': 'Ausgewählte Zahlungsmethode nicht gefunden',
    'vaiu.topup.error.minimum_payment': 'Der Mindestzahlungsbetrag beträgt {amount}',
    'vaiu.topup.error.minimum_topup': 'Der Mindestaufladebetrag beträgt {amount}',
    'vaiu.topup.error.unsupported_method': 'Nicht unterstützter Zahlungsmethodentyp',
    'vaiu.topup.error.load_methods': 'Laden der Zahlungsmethoden fehlgeschlagen',
    'vaiu.topup.error.atm_failed': 'Geldautomaten-Abhebung fehlgeschlagen',
    'vaiu.topup.error.api_error': 'API-Fehler: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Zahlung nicht erfolgreich',
    'vaiu.topup.toast.processing_wallet': 'Verarbeitung von {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Verarbeitung der Zahlung {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Verbindung zum Zahlungsprozessor',
    'vaiu.topup.toast.please_wait': 'Bitte warten...',
    'vaiu.topup.toast.paypal_redirect': 'PayPal-Weiterleitung',
    'vaiu.topup.toast.returning_to': 'Zurück zu: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Bitcoin-Zahlung initiiert',
    'vaiu.topup.toast.bitcoin_complete': 'Bitte schließen Sie die Transaktion in Ihrer Bitcoin-Geldbörse ab.',
  },
  ro: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.ro,
    'vaiu.topup.title': 'Reîncărcare Sold',
    'vaiu.topup.guest_title': 'Finalizați Plata',
    'vaiu.topup.amount_label': 'Suma Reîncărcării ({currency})',
    'vaiu.topup.select_method': 'Selectați Metoda de Plată',
    'vaiu.topup.select_method_placeholder': 'Selectați o metodă de plată',
    'vaiu.topup.add_new_card': 'Adăugați o carte nouă',
    'vaiu.topup.add_bank_account': 'Adăugați un cont bancar',
    'vaiu.topup.no_methods': 'Nu există metode de plată salvate',
    'vaiu.topup.add_method': 'Adăugați Metodă de Plată',
    'vaiu.topup.button': 'Reîncărcați',
    'vaiu.topup.processing': 'Se procesează...',
    'vaiu.topup.payment_amount': 'Suma Plății',
    'vaiu.topup.choose_method': 'Alegeți o metodă de plată',
    'vaiu.topup.digital_wallets': 'Portofele Digitale',
    'vaiu.topup.crypto': 'Cripto',
    'vaiu.topup.shortfall': 'Deficit:',
    'vaiu.topup.tabs.atm': 'Bancomat',
    'vaiu.topup.tabs.card': 'Card',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Portofel',
    'vaiu.topup.atm.description': 'Atingeți cardul dvs. de debit bancar pentru a retrage fonduri din contul dvs. bancar (Bancomat Virtual Digital)',
    'vaiu.topup.crypto.unavailable': 'Plățile cu criptomonede nu sunt disponibile pentru {currency}. Vă rugăm să folosiți o altă metodă de plată.',
    'vaiu.topup.success.payment': 'Plată Reușită',
    'vaiu.topup.success.topup': 'Reîncărcare Reușită',
    'vaiu.topup.success.paid_amount': 'Plată de {amount} realizată cu succes folosind {method}',
    'vaiu.topup.success.balance_added': '{amount} a fost adăugat la soldul dvs. folosind {method}',
    'vaiu.topup.success.card_added': 'Cardul a fost adăugat cu succes',
    'vaiu.topup.success.card_processing': 'Cardul dvs. a fost salvat și plata este procesată.',
    'vaiu.topup.success.charged': '{currency} {amount} a fost încărcat și soldul dvs. a fost actualizat.',
    'vaiu.topup.success.paypal_redirect': 'Redirecționare către PayPal',
    'vaiu.topup.success.paypal_description': 'Veți fi redirecționat către PayPal pentru a finaliza plata.',
    'vaiu.topup.success.atm_paid': 'Plată de {amount} realizată cu succes prin retragere bancomat',
    'vaiu.topup.success.atm_added': '{amount} a fost adăugat la soldul dvs. prin retragere bancomat',
    'vaiu.topup.error.payment_failed': 'Plata a Eșuat',
    'vaiu.topup.error.topup_failed': 'Reîncărcarea a Eșuat',
    'vaiu.topup.error.digital_wallet': 'Eșec la procesarea plății cu portofel digital',
    'vaiu.topup.error.generic': 'Eșec la procesarea plății',
    'vaiu.topup.error.balance': 'Eșec la reîncărcarea soldului',
    'vaiu.topup.error.card': 'Eșec la procesarea noului card. Vă rugăm să încercați din nou.',
    'vaiu.topup.error.atm': 'Eșec la procesarea retragerii bancomat',
    'vaiu.topup.error.select_method': 'Vă rugăm să selectați o metodă de plată',
    'vaiu.topup.error.method_not_found': 'Metoda de plată selectată nu a fost găsită',
    'vaiu.topup.error.minimum_payment': 'Suma minimă de plată este {amount}',
    'vaiu.topup.error.minimum_topup': 'Suma minimă de reîncărcare este {amount}',
    'vaiu.topup.error.unsupported_method': 'Tip de metodă de plată neacceptat',
    'vaiu.topup.error.load_methods': 'Eșec la încărcarea metodelor de plată',
    'vaiu.topup.error.atm_failed': 'Retragerea bancomat a eșuat',
    'vaiu.topup.error.api_error': 'Eroare API: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Plata nu a fost reușită',
    'vaiu.topup.toast.processing_wallet': 'Procesare {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Procesare plată {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Conectare la procesatorul de plăți',
    'vaiu.topup.toast.please_wait': 'Vă rugăm să așteptați...',
    'vaiu.topup.toast.paypal_redirect': 'Redirecționare PayPal',
    'vaiu.topup.toast.returning_to': 'Revenire la: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Plată Bitcoin inițiată',
    'vaiu.topup.toast.bitcoin_complete': 'Vă rugăm să finalizați tranzacția în portofelul dvs. Bitcoin.',
  },
  sr: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.sr,
    'vaiu.topup.title': 'Допуна стања',
    'vaiu.topup.guest_title': 'Завршите ваше плаћање',
    'vaiu.topup.amount_label': 'Износ допуне ({currency})',
    'vaiu.topup.select_method': 'Изаберите начин плаћања',
    'vaiu.topup.select_method_placeholder': 'Изаберите начин плаћања',
    'vaiu.topup.add_new_card': 'Додај нову картицу',
    'vaiu.topup.add_bank_account': 'Додај банковни рачун',
    'vaiu.topup.no_methods': 'Нема сачуваних начина плаћања',
    'vaiu.topup.add_method': 'Додај начин плаћања',
    'vaiu.topup.button': 'Допуни',
    'vaiu.topup.processing': 'Обрада...',
    'vaiu.topup.payment_amount': 'Износ плаћања',
    'vaiu.topup.choose_method': 'Изаберите начин плаћања',
    'vaiu.topup.digital_wallets': 'Дигитални новчаници',
    'vaiu.topup.crypto': 'Крипто',
    'vaiu.topup.shortfall': 'Недостатак:',
    'vaiu.topup.tabs.atm': 'Банкомат',
    'vaiu.topup.tabs.card': 'Картица',
    'vaiu.topup.tabs.paypal': 'ПаиПал',
    'vaiu.topup.tabs.wallet': 'Новчаник',
    'vaiu.topup.atm.description': 'Додирните вашу банковну дебитну картицу да бисте повукли средства са вашег банковног рачуна (Виртуелни дигитални банкомат)',
    'vaiu.topup.crypto.unavailable': 'Крипто плаћања нису доступна за {currency}. Молимо користите други начин плаћања.',
    'vaiu.topup.success.payment': 'Плаћање успешно',
    'vaiu.topup.success.topup': 'Допуна успешна',
    'vaiu.topup.success.paid_amount': 'Успешно плаћено {amount} користећи {method}',
    'vaiu.topup.success.balance_added': '{amount} је додато на ваше стање користећи {method}',
    'vaiu.topup.success.card_added': 'Картица успешно додата',
    'vaiu.topup.success.card_processing': 'Ваша картица је сачувана и плаћање се обрађује.',
    'vaiu.topup.success.charged': '{currency} {amount} је наплаћено и ваше стање је ажурирано.',
    'vaiu.topup.success.paypal_redirect': 'Преусмеравање на ПаиПал',
    'vaiu.topup.success.paypal_description': 'Бићете преусмерени на ПаиПал да завршите плаћање.',
    'vaiu.topup.success.atm_paid': 'Успешно плаћено {amount} преко банкомата',
    'vaiu.topup.success.atm_added': '{amount} је додато на ваше стање преко банкомата',
    'vaiu.topup.error.payment_failed': 'Плаћање неуспешно',
    'vaiu.topup.error.topup_failed': 'Допуна неуспешна',
    'vaiu.topup.error.digital_wallet': 'Није успело обрађивање плаћања дигиталним новчаником',
    'vaiu.topup.error.generic': 'Није успело обрађивање плаћања',
    'vaiu.topup.error.balance': 'Није успела допуна стања',
    'vaiu.topup.error.card': 'Није успело обрађивање нове картице. Молимо покушајте поново.',
    'vaiu.topup.error.atm': 'Није успело обрађивање повлачења са банкомата',
    'vaiu.topup.error.select_method': 'Молимо изаберите начин плаћања',
    'vaiu.topup.error.method_not_found': 'Изабрани начин плаћања није пронађен',
    'vaiu.topup.error.minimum_payment': 'Минимални износ плаћања је {amount}',
    'vaiu.topup.error.minimum_topup': 'Минимални износ допуне је {amount}',
    'vaiu.topup.error.unsupported_method': 'Неподржан тип начина плаћања',
    'vaiu.topup.error.load_methods': 'Није успело учитавање начина плаћања',
    'vaiu.topup.error.atm_failed': 'Повлачење са банкомата није успело',
    'vaiu.topup.error.api_error': 'API грешка: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Плаћање није успело',
    'vaiu.topup.toast.processing_wallet': 'Обрада {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Обрада плаћања {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Повезивање са процесором плаћања',
    'vaiu.topup.toast.please_wait': 'Молимо сачекајте...',
    'vaiu.topup.toast.paypal_redirect': 'Преусмеравање ПаиПал',
    'vaiu.topup.toast.returning_to': 'Повратак на: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Биткоин плаћање покренуто',
    'vaiu.topup.toast.bitcoin_complete': 'Молимо завршите трансакцију у вашем Биткоин новчанику.',
  },
  pl: {
    ...fundingSourceTranslations,
    ...digitalWalletTranslations.pl,
    'vaiu.topup.title': 'Doładuj Saldo',
    'vaiu.topup.guest_title': 'Dokończ Płatność',
    'vaiu.topup.amount_label': 'Kwota Doładowania ({currency})',
    'vaiu.topup.select_method': 'Wybierz Metodę Płatności',
    'vaiu.topup.select_method_placeholder': 'Wybierz metodę płatności',
    'vaiu.topup.add_new_card': 'Dodaj Nową Kartę',
    'vaiu.topup.add_bank_account': 'Dodaj konto bankowe',
    'vaiu.topup.no_methods': 'Brak Zapisanych Metod Płatności',
    'vaiu.topup.add_method': 'Dodaj Metodę Płatności',
    'vaiu.topup.button': 'Doładuj',
    'vaiu.topup.processing': 'Przetwarzanie...',
    'vaiu.topup.payment_amount': 'Kwota Płatności',
    'vaiu.topup.choose_method': 'Wybierz Metodę Płatności',
    'vaiu.topup.digital_wallets': 'Portfele Cyfrowe',
    'vaiu.topup.crypto': 'Krypto',
    'vaiu.topup.shortfall': 'Niedobór:',
    'vaiu.topup.tabs.atm': 'Bankomat',
    'vaiu.topup.tabs.card': 'Karta',
    'vaiu.topup.tabs.paypal': 'PayPal',
    'vaiu.topup.tabs.wallet': 'Portfel',
    'vaiu.topup.atm.description': 'Dotknij Swoją Kartę Debetową, Aby Wypłacić Środki Z Konta Bankowego (Wirtualny Cyfrowy Bankomat)',
    'vaiu.topup.crypto.unavailable': 'Płatności Kryptowalutami Nie Są Dostępne Dla {currency}. Użyj Innej Metody Płatności.',
    'vaiu.topup.success.payment': 'Płatność Udana',
    'vaiu.topup.success.topup': 'Doładowanie Udane',
    'vaiu.topup.success.paid_amount': 'Pomyślnie Zapłacono {amount} Za Pomocą {method}',
    'vaiu.topup.success.balance_added': '{amount} Zostało Dodane Do Twojego Salda Za Pomocą {method}',
    'vaiu.topup.success.card_added': 'Karta Dodana Pomyślnie',
    'vaiu.topup.success.card_processing': 'Twoja Karta Została Zapisana, A Płatność Jest Przetwarzana.',
    'vaiu.topup.success.charged': '{currency} {amount} Zostało Pobrane, A Twoje Saldo Zostało Zaktualizowane.',
    'vaiu.topup.success.paypal_redirect': 'Przekierowanie Do PayPal',
    'vaiu.topup.success.paypal_description': 'Zostaniesz Przekierowany Do PayPal, Aby Dokończyć Płatność.',
    'vaiu.topup.success.atm_paid': 'Pomyślnie Zapłacono {amount} Poprzez Wypłatę Z Bankomatu',
    'vaiu.topup.success.atm_added': '{amount} Zostało Dodane Do Twojego Salda Poprzez Wypłatę Z Bankomatu',
    'vaiu.topup.error.payment_failed': 'Płatność Nieudana',
    'vaiu.topup.error.topup_failed': 'Doładowanie Nieudane',
    'vaiu.topup.error.digital_wallet': 'Nie Udało Się Przetworzyć Płatności Portfelem Cyfrowym',
    'vaiu.topup.error.generic': 'Nie Udało Się Przetworzyć Płatności',
    'vaiu.topup.error.balance': 'Nie Udało Się Doładować Salda',
    'vaiu.topup.error.card': 'Nie Udało Się Przetworzyć Nowej Karty. Spróbuj Ponownie.',
    'vaiu.topup.error.atm': 'Nie Udało Się Przetworzyć Wypłaty Z Bankomatu',
    'vaiu.topup.error.select_method': 'Wybierz Metodę Płatności',
    'vaiu.topup.error.method_not_found': 'Wybrana Metoda Płatności Nie Znaleziona',
    'vaiu.topup.error.minimum_payment': 'Minimalna Kwota Płatności To {amount}',
    'vaiu.topup.error.minimum_topup': 'Minimalna Kwota Doładowania To {amount}',
    'vaiu.topup.error.unsupported_method': 'Nieobsługiwany Typ Metody Płatności',
    'vaiu.topup.error.load_methods': 'Nie Udało Się Załadować Metod Płatności',
    'vaiu.topup.error.atm_failed': 'Wypłata Z Bankomatu Nieudana',
    'vaiu.topup.error.api_error': 'Błąd API: {{error}}',
    'vaiu.topup.error.payment_not_successful': 'Płatność Nie Powiodła Się',
    'vaiu.topup.toast.processing_wallet': 'Przetwarzanie {{walletType}}',
    'vaiu.topup.toast.processing_payment': 'Przetwarzanie płatności {{currency}} {{amount}}...',
    'vaiu.topup.toast.connecting': 'Łączenie z procesorem płatności',
    'vaiu.topup.toast.please_wait': 'Proszę czekać...',
    'vaiu.topup.toast.paypal_redirect': 'Przekierowanie PayPal',
    'vaiu.topup.toast.returning_to': 'Powrót do: {{path}}',
    'vaiu.topup.toast.bitcoin_initiated': 'Płatność Bitcoin Zainicjowana',
    'vaiu.topup.toast.bitcoin_complete': 'Proszę dokończyć transakcję w swoim portfelu Bitcoin.',
  },
};
